import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { take } from 'rxjs';

import { CustomerFeedbackExperienceService } from '@app/services/customer-feedback/customer-feedback-experience.service';
import {
  FeedbackFeatureUpdateSubmitRequest,
  FeedbackPageData,
} from '@core/interfaces/customer-feedback.interface';
import {
  FirstTimeActionFeedbackModalComponent,
  FirstTimeActionFeedbackValue,
} from '@shared/components/first-time-action-feedback-modal/first-time-action-feedback-modal.component';
import { FEATURE_UPDATE_FEEDBACK_CONTENT } from '@app/core/constants/customer-feedback.constants';
import { FeedbackSuccessModalComponent } from '@shared/components/feedback-success-modal/feedback-success-modal.component';
import { buildFeedbackSubmitAnswers } from '@app/utils/customer-feedback.utils';

export type FeatureUpdateFeedbackState =
  | 'loading'
  | 'ready'
  | 'submitting'
  | 'submitted'
  | 'error'
  | 'closed';

@Component({
  selector: 'app-feature-update-feedback',
  standalone: true,
  imports: [CommonModule, FirstTimeActionFeedbackModalComponent, FeedbackSuccessModalComponent],
  templateUrl: './feature-update-feedback.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureUpdateFeedbackComponent implements OnInit {
  readonly UI_CONTENT = FEATURE_UPDATE_FEEDBACK_CONTENT;

  readonly countryId = input.required<number>();
  readonly autoLoad = input<boolean>(true);

  readonly feedbackSubmitted = output<FirstTimeActionFeedbackValue>();
  readonly closed = output<void>();

  readonly isLoading = computed(() => this.state() === 'loading');
  readonly isSubmitting = computed(() => this.state() === 'submitting');
  readonly showSuccessModal = computed(() => this.state() === 'submitted');
  readonly showModal = computed(() => {
    const data = this.pageData();
    return (
      !!data &&
      data.categories.length > 0 &&
      (this.state() === 'ready' || this.state() === 'submitting')
    );
  });

  readonly title = computed(
    () => this.pageData()?.categories?.[0]?.title || this.UI_CONTENT.defaultTitle,
  );
  readonly questions = computed(
    () => this.pageData()?.categories?.[0]?.subCategories?.[0]?.questions ?? [],
  );
  readonly ratingQuestionText = computed(
    () =>
      this.questions()
        .find((question) => question.type === 'rating')
        ?.prompt?.trim() || '',
  );
  readonly yesNoQuestionText = computed(
    () =>
      this.questions()
        .find((question) => question.type === 'boolean')
        ?.prompt?.trim() || '',
  );
  readonly textQuestionText = computed(
    () =>
      this.questions()
        .find((question) => question.type === 'text')
        ?.prompt?.trim() || '',
  );

  private readonly pageData = signal<FeedbackPageData | null>(null);
  private readonly state = signal<FeatureUpdateFeedbackState>('loading');
  private readonly errorMessage = signal<string | null>(null);

  private readonly feedbackService = inject(CustomerFeedbackExperienceService);

  ngOnInit(): void {
    if (this.autoLoad()) {
      this.loadFeedbackConfig();
    }
  }

  onFeedbackSubmitted(feedbackValue: FirstTimeActionFeedbackValue): void {
    const payload = this.buildSubmitPayload(feedbackValue);
    if (!payload) {
      this.state.set('error');
      this.errorMessage.set(this.UI_CONTENT.invalidConfigError);
      return;
    }

    this.state.set('submitting');

    this.feedbackService
      .submitFeatureUpdateFeedback(payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.state.set('submitted');
          this.feedbackSubmitted.emit(feedbackValue);
        },
        error: (error: unknown) => {
          console.error('Failed to submit feature update feedback:', error);
          this.state.set('error');
          this.errorMessage.set(this.UI_CONTENT.submitFailedError);
        },
      });
  }

  onDismissed(): void {
    const categoryId = Number(this.pageData()?.categories?.[0]?.id);
    if (!Number.isFinite(categoryId) || categoryId <= 0) {
      this.onModalClosed();
      return;
    }

    this.feedbackService
      .dismissFeedback({ categoryId })
      .pipe(take(1))
      .subscribe({
        next: () => this.onModalClosed(),
        error: (error: unknown) => {
          console.error('Failed to dismiss feature update feedback:', error);
          this.onModalClosed();
        },
      });
  }

  onModalClosed(): void {
    this.state.set('closed');
    this.closed.emit();
  }

  onCloseSuccessModal(): void {
    this.onModalClosed();
  }

  onRetry(): void {
    this.loadFeedbackConfig();
  }

  errorText(): string | null {
    return this.errorMessage();
  }

  private loadFeedbackConfig(): void {
    this.state.set('loading');
    this.errorMessage.set(null);

    this.feedbackService
      .getFeedbackFeatureUpdateConfig(this.countryId())
      .pipe(take(1))
      .subscribe({
        next: (data: FeedbackPageData) => {
          this.pageData.set(data);
          if (data.categories.length > 0) {
            this.state.set('ready');
            return;
          }

          this.state.set('closed');
          this.closed.emit();
        },
        error: (error: unknown) => {
          console.error('Failed to load feature update feedback config:', error);
          this.state.set('error');
          this.errorMessage.set(this.UI_CONTENT.loadFailedError);
        },
      });
  }

  private buildSubmitPayload(
    feedbackValue: FirstTimeActionFeedbackValue,
  ): FeedbackFeatureUpdateSubmitRequest | null {
    const categoryId = Number(this.pageData()?.categories?.[0]?.id);
    const questions = this.questions();

    if (!Number.isFinite(categoryId) || categoryId <= 0 || questions.length === 0) {
      return null;
    }

    const submittedAnswers = feedbackValue.questionAnswers ?? [];
    const answerByQuestionId = new Map(
      submittedAnswers.map((answer) => [answer.questionId, answer.value]),
    );

    const answers = buildFeedbackSubmitAnswers(questions, answerByQuestionId, {
      resolveSelectedOptionsForSingleSelect: true,
    });

    return {
      categoryId,
      answers,
    };
  }
}
